import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UploadCloud, Palette, Printer, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ChangePasswordCard from "../../components/ChangePasswordCard";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

export default function Settings() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", company: user?.company || "" });
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState({ brand_name: "", primary_color: "#0757D9", support_email: "", support_phone: "", custom_domain: "", logo_url: "" });
  const [docs, setDocs] = useState({ invoice_color: "#0757D9", invoice_footer: "", show_logo: true, label_size: "a4" });
  const [logoFile, setLogoFile] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [savingDocs, setSavingDocs] = useState(false);

  const loadBrand = useCallback(() => {
    api.get("/settings/brand").then(({ data }) => {
      setBrand((b) => ({ ...b, ...data }));
      setDocs({
        invoice_color: data.invoice_color || data.primary_color || "#0757D9",
        invoice_footer: data.invoice_footer || "",
        show_logo: data.show_logo !== false,
        label_size: data.label_size || "a4",
      });
    }).catch(() => {});
  }, []);
  useEffect(() => { loadBrand(); }, [loadBrand]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, phone: form.phone, company: form.company };
      const { data } = await api.put("/settings/profile", payload);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const saveBrand = async (e) => {
    e.preventDefault();
    setSavingBrand(true);
    try {
      await api.put("/settings/brand", {
        brand_name: brand.brand_name, primary_color: brand.primary_color,
        support_email: brand.support_email, support_phone: brand.support_phone, custom_domain: brand.custom_domain,
      });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const { data } = await api.post("/settings/brand/logo", fd);
        setBrand((b) => ({ ...b, logo_url: data.logo_url }));
        setLogoFile(null);
      }
      toast.success("Branded tracking settings saved");
      loadBrand();
    } catch (err) { toast.error(fmtErr(err)); } finally { setSavingBrand(false); }
  };

  const saveDocs = async (e) => {
    e.preventDefault();
    setSavingDocs(true);
    try {
      await api.put("/settings/brand", { ...docs });
      toast.success("Invoice & label settings saved");
    } catch (err) { toast.error(fmtErr(err)); } finally { setSavingDocs(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Settings</h1>
        <p className="text-sm text-slate-500">Profile, branded tracking and document print preferences.</p>
      </div>

      <form onSubmit={save} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-bold text-shipany-navy">Profile & Security</h2>
        <div>
          <label className="text-xs font-semibold text-slate-500">Email</label>
          <input className={`${inputCls} mt-1 bg-slate-50 text-slate-400`} value={user?.email || ""} disabled />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Full name</label>
          <input data-testid="settings-name" className={`${inputCls} mt-1`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-500">Phone</label>
            <input data-testid="settings-phone" className={`${inputCls} mt-1`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Company</label>
            <input data-testid="settings-company" className={`${inputCls} mt-1`} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          </div>
        </div>
        <button data-testid="settings-save-btn" disabled={saving} className="w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>

      <ChangePasswordCard />

      <form onSubmit={saveBrand} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="brand-section">
        <div className="flex items-center justify-between">
          <h2 className="font-display flex items-center gap-2 text-lg font-bold text-shipany-navy"><Palette className="h-5 w-5 text-shipany-blue" /> Branded Tracking Page</h2>
          <Link to="/track" target="_blank" data-testid="brand-preview-link" className="text-xs font-bold text-shipany-blue hover:underline">Preview /track →</Link>
        </div>
        <p className="text-xs text-slate-400">Your customers see your brand — name, logo and color — when they track shipments.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-500">Brand name</label>
            <input data-testid="brand-name-input" className={`${inputCls} mt-1`} value={brand.brand_name || ""} onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })} placeholder="Your store name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Brand color</label>
            <input data-testid="brand-color-input" type="color" className="mt-1 h-[42px] w-full cursor-pointer rounded-xl border border-slate-200 p-1" value={brand.primary_color || "#0757D9"} onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Support email</label>
            <input data-testid="brand-support-email" className={`${inputCls} mt-1`} value={brand.support_email || ""} onChange={(e) => setBrand({ ...brand, support_email: e.target.value })} placeholder="care@yourbrand.com" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Support phone</label>
            <input data-testid="brand-support-phone" className={`${inputCls} mt-1`} value={brand.support_phone || ""} onChange={(e) => setBrand({ ...brand, support_phone: e.target.value })} placeholder="+91 …" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Brand logo (JPG/PNG/WEBP, max 2MB)</label>
          <div className="mt-1 flex items-center gap-3">
            <label data-testid="brand-logo-upload" className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-2.5 text-xs font-semibold text-slate-500 hover:border-shipany-blue hover:text-shipany-blue">
              <UploadCloud className="h-4 w-4" /> {logoFile ? logoFile.name : "Upload logo"}
              <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => setLogoFile(e.target.files[0] || null)} />
            </label>
            {brand.logo_url && <img src={brand.logo_url} alt="brand logo" data-testid="brand-logo-preview" className="h-10 w-auto rounded-lg border border-slate-200 bg-white object-contain px-2 py-1" />}
          </div>
        </div>
        <button data-testid="brand-save-btn" disabled={savingBrand} className="w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
          {savingBrand ? "Saving…" : "Save Branding"}
        </button>
      </form>

      <form onSubmit={saveDocs} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="docs-section">
        <h2 className="font-display flex items-center gap-2 text-lg font-bold text-shipany-navy"><Printer className="h-5 w-5 text-shipany-blue" /> Invoice & Label Print</h2>
        <p className="text-xs text-slate-400">Controls the design of invoices, packing slips and shipping labels on every shipment.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-500">Invoice accent color</label>
            <input data-testid="doc-invoice-color" type="color" className="mt-1 h-[42px] w-full cursor-pointer rounded-xl border border-slate-200 p-1" value={docs.invoice_color} onChange={(e) => setDocs({ ...docs, invoice_color: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Label paper size (printer)</label>
            <select data-testid="doc-label-size" className={`${inputCls} mt-1`} value={docs.label_size} onChange={(e) => setDocs({ ...docs, label_size: e.target.value })}>
              <option value="4x6">4×6 in — Thermal label printer</option>
              <option value="a6">A6 — Compact label printer</option>
              <option value="a5">A5 — Compact printer</option>
              <option value="a4">A4 — Standard printer</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Invoice footer note</label>
          <input data-testid="doc-invoice-footer" className={`${inputCls} mt-1`} value={docs.invoice_footer} onChange={(e) => setDocs({ ...docs, invoice_footer: e.target.value })} placeholder="e.g. Thank you for shopping with us · GSTIN 06AABCA1234F1Z5" />
        </div>
        <label className="flex items-center gap-2.5 text-sm font-medium text-slate-600">
          <input type="checkbox" data-testid="doc-show-logo" checked={docs.show_logo} onChange={(e) => setDocs({ ...docs, show_logo: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
          <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Show logo on invoice & label header
        </label>
        <button data-testid="doc-save-btn" disabled={savingDocs} className="w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
          {savingDocs ? "Saving…" : "Save Document Settings"}
        </button>
      </form>
    </div>
  );
}
